import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, X, ArrowRight } from 'lucide-react';
import Fuse from 'fuse.js';
import { trackEvent } from '@/utils/analytics';
import { searchFaq } from '@/utils/faqMatch';
import { loadFaqIndex } from '@/utils/articles';
import useDebouncedValue from '@/hooks/useDebouncedValue';

const FUSE_OPTIONS = {
  includeScore: true,
  threshold: 0.35,
  ignoreLocation: true,
  minMatchCharLength: 2,
  keys: [
    { name: 'title', weight: 0.6 },
    { name: 'excerpt', weight: 0.3 },
    { name: 'tags', weight: 0.1 },
  ],
};

const MAX_FAQ = 3;
const MAX_ARTICLES = 6;

/**
 * Site-wide modal search.
 *
 * Indexes the supplied `documents` (front-matter records) with Fuse.js for
 * fuzzy matching across title, excerpt, and tags. Up to 8 results are shown,
 * navigable with ↑/↓ + Enter. Closes on Escape or backdrop click.
 *
 * Phase A passes an empty `documents` array — the modal still renders so
 * the keyboard/UX flow is testable. Phase B will wire the real index.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {Array<{ slug: string, title: string, excerpt?: string, tags?: string[], href: string }>} [props.documents=[]]
 *
 * @example
 *   <SearchBox open={open} onClose={close} documents={index} />
 */
export default function SearchBox({ open, onClose, documents = [] }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const panelRef = useRef(null); // conteneur modal — périmètre du focus trap
  const reduce = useReducedMotion();

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  // Index FAQ charge A L'OUVERTURE seulement : il pese 175 Ko gzip et ne sert
  // qu'a ceux qui cherchent vraiment. L'imposer a chaque visiteur annulerait
  // une bonne part du gain de chargement (docs/perf/CHARGEMENT_INITIAL.md).
  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    if (!open) return undefined;
    let vivant = true;
    loadFaqIndex(i18n.language).then((rows) => { if (vivant) setFaqs(rows); });
    return () => { vivant = false; };
  }, [open, i18n.language]);

  const fuse = useMemo(() => (documents.length ? new Fuse(documents, FUSE_OPTIONS) : null), [documents]);

  // Recherche débouncée : la frappe ne doit pas attendre le calcul. 120 ms est
  // sous le seuil de perception tout en fusionnant les rafales de frappe.
  const debounced = useDebouncedValue(query, 120);

  // Le filtre lexical passe AVANT Fuse (cf. searchFaq) : sans lui Fuse matche
  // l'échafaudage interrogatif — « le meilleur restaurant de sushi à Tokyo »
  // remontait « Quel opérateur a le meilleur réseau ? ». Un seuil de score ne
  // peut pas trancher, les distributions se chevauchent.
  const faqHits = useMemo(() => searchFaq(Fuse, faqs, debounced, MAX_FAQ), [faqs, debounced]);

  const articleHits = useMemo(() => {
    if (!fuse || debounced.trim().length < 2) return [];
    return fuse.search(debounced.trim()).slice(0, MAX_ARTICLES).map((r) => r.item);
  }, [fuse, debounced]);

  // Liste PLATE des cibles navigables : les flèches et Entrée doivent parcourir
  // les deux sections d'affilée. Indexer chaque section séparément casserait la
  // navigation clavier au passage de l'une à l'autre.
  const results = useMemo(
    () => [
      ...faqHits.map((item) => ({ kind: 'faq', key: item.id, href: item.href, item })),
      ...articleHits.map((item) => ({ kind: 'article', key: item.slug, href: item.href, item })),
    ],
    [faqHits, articleHits],
  );

  // Reset on open + autofocus the input.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    inputRef.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Keyboard navigation.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      // Focus trap (WCAG 2.1.1 — audit a11y 2026-07-08) : sans ça, Tab sortait
      // de la modale vers la page floutée derrière. Pattern combobox : seuls
      // l'input et le bouton fermer sont tabbables (les résultats se parcourent
      // aux flèches) → on cycle entre les focusables du panneau, comme MobileMenu.
      if (e.key === 'Tab') {
        const panel = panelRef.current;
        if (!panel) return;
        const focusables = panel.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled])',
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      if (!results.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % results.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + results.length) % results.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const target = results[activeIndex];
        if (target) {
          navigate(target.href);
          onClose();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, results, activeIndex, navigate, onClose]);

  // Keep active result scrolled into view.
  useEffect(() => {
    if (!listRef.current) return;
    const node = listRef.current.querySelector(`[data-idx="${activeIndex}"]`);
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // GA4 `search` event — débounce, une fois par terme stabilisé (≥ 2 car.).
  // No-op tant que GA n'est pas initialisé (consentement). Capte aussi les
  // recherches sans résultat (intention), contrairement à un tracking au clic.
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) return undefined;
    const id = setTimeout(() => trackEvent('search', { search_term: term }), 700);
    return () => clearTimeout(id);
  }, [query]);

  // Base sur la valeur DEBOUNCEE : sur `query`, l'etat vide clignoterait
  // pendant les 120 ms ou les resultats ne sont pas encore recalcules.
  const showEmpty = debounced.trim().length >= 2 && results.length === 0;

  // Quand rien ne matche, on ne laisse pas le lecteur dans le vide : seconde
  // passe volontairement laxiste pour proposer les articles les moins loin.
  // Dire « aucune réponse sourcée » sans rien offrir serait honnête mais inutile.
  const fallbackArticles = useMemo(() => {
    if (!showEmpty || !documents.length) return [];
    const loose = new Fuse(documents, { ...FUSE_OPTIONS, threshold: 0.6 });
    return loose.search(debounced.trim()).slice(0, 3).map((r) => r.item);
  }, [showEmpty, documents, debounced]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={t('common:search')}
          className="fixed inset-0 z-50 flex items-start justify-center bg-navy-900/70 px-4 pt-24 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <motion.div
            ref={panelRef}
            initial={reduce ? false : { y: -16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { y: -16, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="w-full max-w-xl overflow-hidden rounded-card bg-cream-50 shadow-card-hover dark:bg-zinc-950 dark:shadow-none dark:ring-1 dark:ring-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <label className="flex items-center gap-3 border-b border-navy-900/10 px-5 py-4 dark:border-white/10">
              <Search size={20} className="text-gray-700 dark:text-white/60" aria-hidden="true" />
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
                role="combobox"
                aria-expanded={results.length > 0}
                aria-controls="search-results"
                aria-autocomplete="list"
                className="flex-1 border-0 bg-transparent text-base text-navy-900 placeholder-gray-500 focus:outline-none focus:ring-0 dark:text-white dark:placeholder-white/40"
                placeholder={t('components:search.placeholder')}
              />
              <button
                type="button"
                onClick={onClose}
                aria-label={t('navigation:closeMenu')}
                className="rounded-btn p-1.5 text-gray-700 hover:bg-cream-100 dark:text-white/70 dark:hover:bg-white/10"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </label>

            <div
              ref={listRef}
              id="search-results"
              role="listbox"
              className="max-h-[60vh] overflow-y-auto"
            >
              {results.length > 0 && (
                <ul className="py-2">
                  {results.map((row, idx) => {
                    const active = idx === activeIndex;
                    // En-tête de section, posé sur la 1re ligne de chaque groupe.
                    // `results` reste plat pour le clavier ; seul l'affichage groupe.
                    const header =
                      idx === 0 && row.kind === 'faq'
                        ? t('components:search.answersHeading')
                        : idx === faqHits.length && row.kind === 'article' && faqHits.length > 0
                          ? t('components:search.articlesHeading')
                          : null;
                    return (
                      <li key={row.key} role="option" aria-selected={active} data-idx={idx}>
                        {header && (
                          <p className="v4-label-mono px-5 pb-1 pt-3 text-gray-700 dark:text-white/50" aria-hidden="true">
                            {header}
                          </p>
                        )}
                        <button
                          type="button"
                          onMouseEnter={() => setActiveIndex(idx)}
                          onClick={() => {
                            navigate(row.href);
                            onClose();
                          }}
                          className={`flex w-full items-start gap-3 px-5 py-3 text-left transition-colors ${
                            active ? 'bg-cream-100 dark:bg-white/10' : 'hover:bg-cream-100/60 dark:hover:bg-white/5'
                          }`}
                        >
                          {row.kind === 'faq' ? (
                            <div className="flex-1 border-l-2 border-gold-500 pl-3">
                              <p className="font-serif text-base text-navy-900 dark:text-white">{row.item.question}</p>
                              <p className="mt-1 line-clamp-3 text-sm leading-relaxed text-gray-700 dark:text-white/70">
                                {row.item.answer}
                              </p>
                              <p className="mt-1.5 text-[11px] text-gray-700/80 dark:text-white/50">
                                {t('components:search.answerSource', { title: row.item.articleTitle })}
                              </p>
                            </div>
                          ) : (
                            <div className="flex-1">
                              <p className="font-serif text-base font-semibold text-navy-900 dark:text-white">
                                {row.item.title}
                              </p>
                              {row.item.excerpt && (
                                <p className="mt-0.5 line-clamp-2 text-sm text-gray-700 dark:text-white/60">
                                  {row.item.excerpt}
                                </p>
                              )}
                            </div>
                          )}
                          <ArrowRight size={16} className="mt-1 shrink-0 text-gray-700 dark:text-white/60" aria-hidden="true" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {showEmpty && (
                <div className="px-5 py-6">
                  <p className="text-sm text-gray-700 dark:text-white/60">{t('components:search.empty')}</p>
                  {fallbackArticles.length > 0 && (
                    <>
                      <p className="v4-label-mono mt-4 text-gray-700 dark:text-white/50">
                        {t('components:search.nearbyHeading')}
                      </p>
                      <ul className="mt-1.5 space-y-1">
                        {fallbackArticles.map((doc) => (
                          <li key={doc.slug}>
                            <button
                              type="button"
                              onClick={() => {
                                navigate(doc.href);
                                onClose();
                              }}
                              className="text-left text-sm text-gold-500 underline-offset-2 hover:underline dark:text-gold-400"
                            >
                              {doc.title}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
              {query.trim().length < 2 && (
                <p className="px-5 py-6 text-sm text-gray-700 dark:text-white/60">{t('common:searchPlaceholder')}</p>
              )}
            </div>

            <div className="border-t border-navy-900/10 bg-cream-100 px-5 py-2.5 text-[11px] text-gray-700 dark:border-white/10 dark:bg-zinc-900 dark:text-white/60">
              {t('components:search.kbdHint')}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
